import type { LanguageModelV2Prompt } from '@ai-sdk/provider-v5';
import type { LanguageModelV1Prompt, CoreMessage as CoreMessageV4 } from '@internal/ai-sdk-v4';
import type * as AIV4Type from '@internal/ai-sdk-v4';
import { v4 as randomUUID } from '@lukeed/uuid';

import { MastraError, ErrorDomain, ErrorCategory } from '../../error';
import type { IMastraLogger } from '../../logger';
import { getTransformedToolPayload, hasTransformedToolPayload } from '../../tools/payload-transform';
import type { IdGeneratorContext } from '../../types';
import { createSignal, isCreatedAgentSignal, mastraDBMessageToSignal } from '../signals';
import type { CreatedAgentSignal } from '../signals';
import { AIV4Adapter, AIV5Adapter, AIV6Adapter } from './adapters';
import { CacheKeyGenerator } from './cache/CacheKeyGenerator';
import {
  aiV4CoreMessageToV1PromptMessage,
  aiV5ModelMessageToV2PromptMessage,
  aiV5PromptToAIV6Prompt,
  coreContentToString,
  messagesAreEqual,
  inputToMastraDBMessage as convertInputToMastraDBMessage,
  aiV4UIMessagesToAIV4CoreMessages,
  aiV5UIMessagesToAIV5ModelMessages as convertAIV5UIToModelMessages,
  aiV4CoreMessagesToAIV5ModelMessages as convertAIV4CoreToAIV5ModelMessages,
  systemMessageToAIV4Core,
  StepContentExtractor,
} from './conversion';
import { TypeDetector } from './detection/TypeDetector';
import { MessageMerger } from './merge';
import { convertImageFilePart } from './prompt/convert-file';
import { convertToV1Messages } from './prompt/convert-to-mastra-v1';
import { downloadAssetsFromMessages } from './prompt/download-assets';
import { MessageStateManager } from './state';
import type {
  MastraDBMessage,
  MastraMessagePart,
  MastraMessageV1,
  MessageSource,
  MemoryInfo,
  UIMessageWithMetadata,
  SerializedMessageListState,
} from './state';
import type { AIV5Type, AIV5ResponseMessage, AIV6Type, MessageInput, MessageListInput } from './types';
import { ensureGeminiCompatibleMessages } from './utils/provider-compat';
import { stampPart } from './utils/stamp-part';

function isSignalDataMessage<T extends { role: string; parts: Array<{ type: string }> }>(message: T): boolean {
  return message.role === 'system' && message.parts.length > 0 && message.parts.every(p => p.type.startsWith('data-'));
}

/**
 * Post-processes converted UI messages to merge non-user signal data parts into an
 * immediate neighbor assistant message, matching active-streaming behavior.
 *
 * Only checks immediate neighbors: append to preceding assistant, or prepend to
 * following assistant. If neither neighbor is assistant, convert the signal in-place
 * to an assistant message with just its data parts.
 */
function mergeSignalDataParts<T extends { role: string; parts: Array<{ type: string }> }>(messages: T[]): T[] {
  const result: T[] = [];
  for (let idx = 0; idx < messages.length; idx++) {
    const message = messages[idx]!;
    if (!isSignalDataMessage(message)) {
      result.push(message);
      continue;
    }

    const prev = result[result.length - 1];
    const next = messages[idx + 1];

    if (prev && prev.role === 'assistant') {
      result[result.length - 1] = { ...prev, parts: [...prev.parts, ...message.parts] };
    } else if (next && next.role === 'assistant') {
      messages[idx + 1] = { ...next, parts: [...message.parts, ...next.parts] } as T;
    } else {
      result.push({ ...message, role: 'assistant' } as T);
    }
  }
  return result;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mergeBackgroundTasks(
  existingBgTasks?: Record<string, unknown>,
  incomingBgTasks?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!existingBgTasks && !incomingBgTasks) {
    return undefined;
  }

  const merged: Record<string, unknown> = { ...(existingBgTasks ?? {}) };
  for (const [toolCallId, incomingTask] of Object.entries(incomingBgTasks ?? {})) {
    const existingTask = merged[toolCallId];
    merged[toolCallId] =
      isPlainRecord(existingTask) && isPlainRecord(incomingTask) ? { ...existingTask, ...incomingTask } : incomingTask;
  }
  return merged;
}

type MessageListAddOptions = {
  merge?: boolean;
};

export class MessageList {
  private messages: MastraDBMessage[] = [];

  // passed in by dev in input or context
  private systemMessages: AIV4Type.CoreSystemMessage[] = [];
  // passed in by us for a specific purpose, eg memory system message
  private taggedSystemMessages: Record<string, AIV4Type.CoreSystemMessage[]> = {};

  private memoryInfo: null | MemoryInfo = null;

  // Centralized state management for message tracking
  private stateManager = new MessageStateManager();

  // Legacy getters for backward compatibility - delegate to stateManager
  private get memoryMessages() {
    return this.stateManager.getMemoryMessages();
  }
  private get newUserMessages() {
    return this.stateManager.getUserMessages();
  }
  private get newResponseMessages() {
    return this.stateManager.getResponseMessages();
  }
  private get userContextMessages() {
    return this.stateManager.getContextMessages();
  }
  private get memoryMessagesPersisted() {
    return this.stateManager.getMemoryMessagesPersisted();
  }
  private get newUserMessagesPersisted() {
    return this.stateManager.getUserMessagesPersisted();
  }
  private get newResponseMessagesPersisted() {
    return this.stateManager.getResponseMessagesPersisted();
  }
  private get userContextMessagesPersisted() {
    return this.stateManager.getContextMessagesPersisted();
  }

  private generateMessageId?: (context?: IdGeneratorContext) => string;
  private _agentNetworkAppend = false;
  private filterIncompleteToolCalls: boolean;
  private logger?: IMastraLogger;

  private toAIV5UIMessages(messages: MastraDBMessage[], options?: { transformToolPayloads?: boolean }) {
    return mergeSignalDataParts(messages.map(message => AIV5Adapter.toUIMessage(message, options)));
  }

  private toAIV4UIMessages(messages: MastraDBMessage[], options?: { transformToolPayloads?: boolean }) {
    return mergeSignalDataParts(messages.map(message => AIV4Adapter.toUIMessage(message, options)));
  }

  private toAIV6UIMessages(messages: MastraDBMessage[]) {
    return mergeSignalDataParts(messages.map(AIV6Adapter.toUIMessage));
  }

  // Event recording for observability
  private isRecording = false;
  private recordedEvents: Array<{
    type: 'add' | 'addSystem' | 'removeByIds' | 'clear';
    source?: MessageSource;
    count?: number;
    ids?: string[];
    text?: string;
    tag?: string;
    message?: CoreMessageV4;
  }> = [];

  constructor({
    threadId,
    resourceId,
    generateMessageId,
    logger,
    filterIncompleteToolCalls,
    // @ts-expect-error Flag for agent network messages
    _agentNetworkAppend,
  }: {
    threadId?: string;
    resourceId?: string;
    generateMessageId?: (context?: IdGeneratorContext) => string;
    logger?: IMastraLogger;
    filterIncompleteToolCalls?: boolean;
  } = {}) {
    if (threadId) {
      this.memoryInfo = { threadId, resourceId };
    }
    this.generateMessageId = generateMessageId;
    this.logger = logger;
    this.filterIncompleteToolCalls = filterIncompleteToolCalls ?? true;
    this._agentNetworkAppend = _agentNetworkAppend || false;
  }

  /**
   * Start recording mutations to the MessageList for observability/tracing
   */
  public startRecording(): void {
    this.isRecording = true;
    this.recordedEvents = [];
  }

  public hasRecordedEvents(): boolean {
    return this.recordedEvents.length > 0;
  }

  public getRecordedEvents(): Array<{
    type: 'add' | 'addSystem' | 'removeByIds' | 'clear';
    source?: MessageSource;
    count?: number;
    ids?: string[];
    text?: string;
    tag?: string;
    message?: CoreMessageV4;
  }> {
    const events = [...this.recordedEvents];
    return events;
  }

  /**
   * Stop recording and return the list of recorded events
   */
  public stopRecording(): Array<{
    type: 'add' | 'addSystem' | 'removeByIds' | 'clear';
    source?: MessageSource;
    count?: number;
    ids?: string[];
    text?: string;
    tag?: string;
    message?: CoreMessageV4;
  }> {
    this.isRecording = false;
    const events = this.getRecordedEvents();
    this.recordedEvents = [];
    return events;
  }

  public addSignal(signal: CreatedAgentSignal, options?: { source?: MessageSource }): CreatedAgentSignal {
    const source = options?.source ?? 'input';
    const createdAt = this.generateCreatedAt(source, new Date());
    const acceptedAt = signal.acceptedAt ?? signal.createdAt;
    const signalForTranscript = createSignal({
      id: signal.id,
      type: signal.type,
      tagName: signal.tagName,
      contents: signal.contents,
      attributes: signal.attributes,
      metadata: signal.metadata,
      providerOptions: signal.providerOptions,
      createdAt,
      acceptedAt,
    });

    this.addOne(signalForTranscript.toDBMessage(this.memoryInfo ?? undefined), source);
    return signalForTranscript;
  }

  public add(messages: MessageListInput, messageSource: MessageSource, options: MessageListAddOptions = {}) {
    if (messageSource === `user`) messageSource = `input`;

    if (!messages) return this;
    const messageArray = Array.isArray(messages) ? messages : [messages];

    // Record event if recording is enabled
    if (this.isRecording) {
      this.recordedEvents.push({
        type: 'add',
        source: messageSource,
        count: messageArray.length,
      });
    }

    for (const message of messageArray) {
      if (isCreatedAgentSignal(message) && messageSource === 'input') {
        this.addSignal(message, { source: messageSource });
        continue;
      }

      const messageInput = isCreatedAgentSignal(message)
        ? message.toDBMessage(this.memoryInfo ?? undefined)
        : typeof message === `string`
          ? {
              role: 'user' as const,
              content: message,
            }
          : message;

      if (Array.isArray(messageInput)) {
        for (const nestedMessage of messageInput) {
          this.addOne(
            typeof nestedMessage === `string`
              ? {
                  role: 'user',
                  content: nestedMessage,
                }
              : nestedMessage,
            messageSource,
            options,
          );
        }
        continue;
      }

      this.addOne(
        typeof messageInput === `string`
          ? {
              role: 'user',
              content: messageInput,
            }
          : messageInput,
        messageSource,
        options,
      );
    }
    return this;
  }

  public serialize(): SerializedMessageListState {
    return this.stateManager.serializeAll({
      messages: this.messages,
      systemMessages: this.systemMessages,
      taggedSystemMessages: this.taggedSystemMessages,
      memoryInfo: this.memoryInfo,
      agentNetworkAppend: this._agentNetworkAppend,
    });
  }

  /**
   * Custom serialization for tracing/observability spans.
   * Returns a clean representation with just the essential data,
   * excluding internal state tracking, methods, and implementation details.
   *
   * This is automatically called by the span serialization system when
   * a MessageList instance appears in span input/output/attributes.
   */
  public serializeForSpan(): {
    messages: Array<{ role: string; content: unknown }>;
    systemMessages: Array<{ role: string; content: unknown; tag?: string }>;
  } {
    const coreMessages = this.all.aiV4.core();

    return {
      messages: coreMessages.map(msg => ({
        role: msg.role,
        content: msg.content,
      })),
      systemMessages: [
        // Untagged first (base instructions)
        ...this.systemMessages.map(m => ({ role: m.role, content: m.content })),
        // Tagged after (contextual additions)
        ...Object.entries(this.taggedSystemMessages).flatMap(([tag, msgs]) =>
          msgs.map(m => ({ role: m.role, content: m.content, tag })),
        ),
      ],
    };
  }

  public deserialize(state: SerializedMessageListState) {
    const data = this.stateManager.deserializeAll(state);
    this.messages = data.messages;
    this.systemMessages = data.systemMessages;
    this.taggedSystemMessages = data.taggedSystemMessages;
    this.memoryInfo = data.memoryInfo;
    this._agentNetworkAppend = data.agentNetworkAppend;
    for (const message of this.messages) {
      this.updateLastCreatedAt(message);
    }
    return this;
  }

  private getMessagesForModelPrompt(): MastraDBMessage[] {
    return this.messages.flatMap(message => {
      if ((message.role as string) !== 'signal') {
        return [message];
      }

      return this.convertSignalForModelPrompt(message);
    });
  }

  private convertSignalForModelPrompt(message: MastraDBMessage): MastraDBMessage[] {
    // Model providers only understand normal prompt messages, so project the signal into
    // its LLM-facing UserModelMessage. Preserve the original id/createdAt so MessageList's
    // timestamp/ordering bookkeeping stays anchored to the persisted signal row.
    const signalMessage = mastraDBMessageToSignal(message).toLLMMessage();
    const createdAt = message.createdAt;
    const promptMessage = {
      ...signalMessage,
      id: message.id,
      metadata: { createdAt },
    };

    return [
      convertInputToMastraDBMessage(promptMessage as MessageInput, 'input', {
        memoryInfo: this.memoryInfo,
        newMessageId: () => message.id,
        generateCreatedAt: (_messageSource, start) => {
          if (start instanceof Date) return start;
          if (typeof start === 'string' || typeof start === 'number') return new Date(start);
          return createdAt;
        },
        dbMessages: this.messages,
      }),
    ];
  }

  public makeMessageSourceChecker(): {
    memory: Set<string>;
    input: Set<string>;
    output: Set<string>;
    context: Set<string>;
    getSource: (message: MastraDBMessage) => MessageSource | null;
  } {
    return this.stateManager.createSourceChecker();
  }

  public getLatestUserContent(): string | null {
    const currentUserMessages = this.all.core().filter(m => m.role === 'user');
    const content = currentUserMessages.at(-1)?.content;
    if (!content) return null;
    return coreContentToString(content);
  }

  public get get() {
    return {
      all: this.all,
      remembered: this.remembered,
      input: this.input,
      response: this.response,
    };
  }
  public get getPersisted() {
    return {
      remembered: this.rememberedPersisted,
      input: this.inputPersisted,
      taggedSystemMessages: this.taggedSystemMessages,
      response: this.responsePersisted,
    };
  }

  public get clear() {
    return {
      all: {
        db: (): MastraDBMessage[] => {
          const allMessages = [...this.messages];
          this.messages = [];
          this.stateManager.clearAll();
          if (this.isRecording && allMessages.length > 0) {
            this.recordedEvents.push({
              type: 'clear',
              count: allMessages.length,
            });
          }
          return allMessages;
        },
      },
      input: {
        db: (): MastraDBMessage[] => {
          const userMessages = Array.from(this.stateManager.getUserMessages());
          this.messages = this.messages.filter(m => !this.stateManager.isUserMessage(m));
          this.stateManager.clearUserMessages();
          if (this.isRecording && userMessages.length > 0) {
            this.recordedEvents.push({
              type: 'clear',
              source: 'input',
              count: userMessages.length,
            });
          }
          return userMessages;
        },
      },
      response: {
        db: () => {
          const responseMessages = Array.from(this.stateManager.getResponseMessages());
          this.messages = this.messages.filter(m => !this.stateManager.isResponseMessage(m));
          this.stateManager.clearResponseMessages();
          if (this.isRecording && responseMessages.length > 0) {
            this.recordedEvents.push({
              type: 'clear',
              source: 'response',
              count: responseMessages.length,
            });
          }
          return responseMessages;
        },
      },
    };
  }

  /**
   * Remove messages by ID
   * @param ids - Array of message IDs to remove
   * @returns Array of removed messages
   */
  public removeByIds(ids: string[]): MastraDBMessage[] {
    const idsSet = new Set(ids);
    const removed: MastraDBMessage[] = [];
    this.messages = this.messages.filter(m => {
      if (idsSet.has(m.id)) {
        removed.push(m);
        this.stateManager.removeMessage(m);
        return false;
      }
      return true;
    });
    if (this.isRecording && removed.length > 0) {
      this.recordedEvents.push({
        type: 'removeByIds',
        ids,
        count: removed.length,
      });
    }
    return removed;
  }

  private all = {
    db: (): MastraDBMessage[] => this.messages,
    v1: (): MastraMessageV1[] => convertToV1Messages(this.all.db()),

    aiV5: {
      model: (): AIV5Type.ModelMessage[] => {
        const promptMessages = this.getMessagesForModelPrompt();
        return convertAIV5UIToModelMessages(
          this.toAIV5UIMessages(promptMessages, { transformToolPayloads: false }),
          promptMessages,
        );
      },
      ui: (): AIV5Type.UIMessage[] => this.toAIV5UIMessages(this.all.db()),

      // Used when calling AI SDK streamText/generateText
      prompt: (): AIV5Type.ModelMessage[] => {
        const systemMessages = convertAIV4CoreToAIV5ModelMessages(
          [...this.systemMessages, ...Object.values(this.taggedSystemMessages).flat()],
          `system`,
          this.createAdapterContext(),
          this.messages,
        );
        const promptMessages = this.getMessagesForModelPrompt();
        const modelMessages = convertAIV5UIToModelMessages(
          this.toAIV5UIMessages(promptMessages, { transformToolPayloads: false }),
          promptMessages,
          this.filterIncompleteToolCalls,
        );

        const messages = [...systemMessages, ...modelMessages];

        return ensureGeminiCompatibleMessages(messages, this.logger);
      },

      // Used for creating LLM prompt messages without AI SDK streamText/generateText
      llmPrompt: async (
        options: {
          downloadConcurrency?: number;
          downloadRetries?: number;
          supportedUrls?: Record<string, RegExp[]>;
        } = {
          downloadConcurrency: 10,
          downloadRetries: 3,
        },
      ): Promise<LanguageModelV2Prompt> => {
        const promptMessages = this.getMessagesForModelPrompt();
        const modelMessages = convertAIV5UIToModelMessages(
          this.toAIV5UIMessages(promptMessages, { transformToolPayloads: false }),
          promptMessages,
          this.filterIncompleteToolCalls,
        );

        const storedModelOutputs = new Map<string, unknown>();
        for (const dbMsg of this.messages) {
          if (dbMsg.content?.format !== 2 || !dbMsg.content.parts) continue;

          for (const part of dbMsg.content.parts) {
            if (
              part.type === 'tool-invocation' &&
              part.toolInvocation?.state === 'result' &&
              part.providerMetadata?.mastra &&
              typeof part.providerMetadata.mastra === 'object' &&
              'modelOutput' in (part.providerMetadata.mastra as Record<string, unknown>)
            ) {
              storedModelOutputs.set(
                part.toolInvocation.toolCallId,
                (part.providerMetadata.mastra as Record<string, unknown>).modelOutput,
              );
            }
          }
        }

        if (storedModelOutputs.size > 0) {
          for (const modelMsg of modelMessages) {
            if (modelMsg.role !== 'tool' || !Array.isArray(modelMsg.content)) continue;

            for (let i = 0; i < modelMsg.content.length; i++) {
              const part = modelMsg.content[i]!;
              if (part.type === 'tool-result' && storedModelOutputs.has(part.toolCallId)) {
                modelMsg.content[i] = {
                  ...part,
                  output: storedModelOutputs.get(part.toolCallId) as any,
                };
              }
            }
          }
        }
        const systemMessages = convertAIV4CoreToAIV5ModelMessages(
          [...this.systemMessages, ...Object.values(this.taggedSystemMessages).flat()],
          `system`,
          this.createAdapterContext(),
          this.messages,
        );

        const downloadedAssets = await downloadAssetsFromMessages({
          messages: modelMessages,
          downloadConcurrency: options?.downloadConcurrency,
          downloadRetries: options?.downloadRetries,
          supportedUrls: options?.supportedUrls,
        });

        let messages = [...systemMessages, ...modelMessages];

        // Check if any messages have image/file content that needs processing
        const hasImageOrFileContent = modelMessages.some(
          message =>
            (message.role === 'user' || message.role === 'assistant') &&
            typeof message.content !== 'string' &&
            message.content.some(part => part.type === 'image' || part.type === 'file'),
        );

        if (hasImageOrFileContent) {
          messages = messages.map(message => {
            if (message.role === 'user') {
              if (typeof message.content === 'string') {
                return {
                  role: 'user' as const,
                  content: [{ type: 'text' as const, text: message.content }],
                  providerOptions: message.providerOptions,
                } as AIV5Type.ModelMessage;
              }

              const convertedContent = message.content
                .map(part => {
                  if (part.type === 'image' || part.type === 'file') {
                    return convertImageFilePart(part, downloadedAssets);
                  }
                  return part;
                })
                .filter(part => part.type !== 'text' || part.text !== '');

              return {
                role: 'user' as const,
                content: convertedContent,
                providerOptions: message.providerOptions,
              } as AIV5Type.ModelMessage;
            }

            if (message.role === 'assistant' && typeof message.content !== 'string') {
              const convertedContent = message.content.map(part => {
                if (part.type === 'file') {
                  return convertImageFilePart(part, downloadedAssets);
                }
                return part;
              });

              return {
                ...message,
                content: convertedContent,
              };
            }

            return message;
          });
        }

        messages = ensureGeminiCompatibleMessages(messages, this.logger);

        return messages
          .map(aiV5ModelMessageToV2PromptMessage)
          .filter(
            message => message.role === 'system' || typeof message.content === 'string' || message.content.length > 0,
          );
      },
    },
    aiV6: {
      ui: () => this.toAIV6UIMessages(this.all.db()),

      // Builds the v5 prompt, then converts it to the shape AI SDK v6 (spec 'v3')
      // providers require (tool-result `media` -> `image-data`/`file-data`).
      llmPrompt: async (options?: {
        downloadConcurrency?: number;
        downloadRetries?: number;
        supportedUrls?: Record<string, RegExp[]>;
      }): Promise<LanguageModelV2Prompt> => aiV5PromptToAIV6Prompt(await this.all.aiV5.llmPrompt(options)),
    },

    /* @deprecated use list.get.all.aiV4.prompt() instead */
    prompt: () => this.all.aiV4.prompt(),
    /* @deprecated use list.get.all.aiV4.ui() */
    ui: (): UIMessageWithMetadata[] => this.toAIV4UIMessages(this.all.db()),
    /* @deprecated use list.get.all.aiV4.core() */
    core: (): CoreMessageV4[] =>
      aiV4UIMessagesToAIV4CoreMessages(this.toAIV4UIMessages(this.all.db(), { transformToolPayloads: false })),
    aiV4: {
      ui: (): UIMessageWithMetadata[] => this.toAIV4UIMessages(this.all.db()),
      core: (): CoreMessageV4[] =>
        aiV4UIMessagesToAIV4CoreMessages(
          this.toAIV4UIMessages(this.getMessagesForModelPrompt(), { transformToolPayloads: false }),
        ),

      // Used when calling AI SDK streamText/generateText
      prompt: () => {
        const coreMessages = this.all.aiV4.core();
        const messages = [...this.systemMessages, ...Object.values(this.taggedSystemMessages).flat(), ...coreMessages];

        return ensureGeminiCompatibleMessages(messages, this.logger);
      },

      // Used for creating LLM prompt messages without AI SDK streamText/generateText
      llmPrompt: (): LanguageModelV1Prompt => {
        const coreMessages = this.all.aiV4.core();

        const systemMessages = [...this.systemMessages, ...Object.values(this.taggedSystemMessages).flat()];
        let messages = [...systemMessages, ...coreMessages];

        messages = ensureGeminiCompatibleMessages(messages, this.logger);

        return messages.map(aiV4CoreMessageToV1PromptMessage);
      },
    },
  };

  private remembered = {
    db: () => this.messages.filter(m => this.memoryMessages.has(m)),
    v1: () => convertToV1Messages(this.remembered.db()),

    aiV5: {
      model: () =>
        convertAIV5UIToModelMessages(
          this.toAIV5UIMessages(this.remembered.db(), { transformToolPayloads: false }),
          this.messages,
        ),
      ui: (): AIV5Type.UIMessage[] => this.toAIV5UIMessages(this.remembered.db()),
    },
    aiV6: {
      ui: () => this.toAIV6UIMessages(this.remembered.db()),
    },

    /* @deprecated use list.get.remembered.aiV4.ui() */
    ui: (): UIMessageWithMetadata[] => this.toAIV4UIMessages(this.remembered.db()),
    /* @deprecated use list.get.remembered.aiV4.core() */
    core: (): CoreMessageV4[] =>
      aiV4UIMessagesToAIV4CoreMessages(this.toAIV4UIMessages(this.remembered.db(), { transformToolPayloads: false })),
    aiV4: {
      ui: (): UIMessageWithMetadata[] => this.toAIV4UIMessages(this.remembered.db()),
      core: (): CoreMessageV4[] =>
        aiV4UIMessagesToAIV4CoreMessages(this.toAIV4UIMessages(this.remembered.db(), { transformToolPayloads: false })),
    },
  };
  private rememberedPersisted = {
    db: () => this.all.db().filter(m => this.memoryMessagesPersisted.has(m)),
    v1: () => convertToV1Messages(this.rememberedPersisted.db()),

    aiV5: {
      model: () =>
        convertAIV5UIToModelMessages(
          this.toAIV5UIMessages(this.rememberedPersisted.db(), { transformToolPayloads: false }),
          this.messages,
        ),
      ui: (): AIV5Type.UIMessage[] => this.toAIV5UIMessages(this.rememberedPersisted.db()),
    },
    aiV6: {
      ui: () => this.toAIV6UIMessages(this.rememberedPersisted.db()),
    },

    /* @deprecated use list.getPersisted.remembered.aiV4.ui() */
    ui: () => this.toAIV4UIMessages(this.rememberedPersisted.db()),
    /* @deprecated use list.getPersisted.remembered.aiV4.core() */
    core: () =>
      aiV4UIMessagesToAIV4CoreMessages(
        this.toAIV4UIMessages(this.rememberedPersisted.db(), { transformToolPayloads: false }),
      ),
    aiV4: {
      ui: (): UIMessageWithMetadata[] => this.toAIV4UIMessages(this.rememberedPersisted.db()),
      core: (): CoreMessageV4[] =>
        aiV4UIMessagesToAIV4CoreMessages(
          this.toAIV4UIMessages(this.rememberedPersisted.db(), { transformToolPayloads: false }),
        ),
    },
  };

  private input = {
    db: () => this.messages.filter(m => this.newUserMessages.has(m)),
    v1: () => convertToV1Messages(this.input.db()),

    aiV5: {
      model: () =>
        convertAIV5UIToModelMessages(
          this.toAIV5UIMessages(this.input.db(), { transformToolPayloads: false }),
          this.messages,
        ),
      ui: (): AIV5Type.UIMessage[] => this.toAIV5UIMessages(this.input.db()),
    },
    aiV6: {
      ui: () => this.toAIV6UIMessages(this.input.db()),
    },

    /* @deprecated use list.get.input.aiV4.ui() instead */
    ui: () => this.toAIV4UIMessages(this.input.db()),
    /* @deprecated use list.get.core.aiV4.ui() instead */
    core: () =>
      aiV4UIMessagesToAIV4CoreMessages(this.toAIV4UIMessages(this.input.db(), { transformToolPayloads: false })),
    aiV4: {
      ui: (): UIMessageWithMetadata[] => this.toAIV4UIMessages(this.input.db()),
      core: (): CoreMessageV4[] =>
        aiV4UIMessagesToAIV4CoreMessages(this.toAIV4UIMessages(this.input.db(), { transformToolPayloads: false })),
    },
  };
  private inputPersisted = {
    db: (): MastraDBMessage[] => this.messages.filter(m => this.newUserMessagesPersisted.has(m)),
    v1: (): MastraMessageV1[] => convertToV1Messages(this.inputPersisted.db()),

    aiV5: {
      model: () =>
        convertAIV5UIToModelMessages(
          this.toAIV5UIMessages(this.inputPersisted.db(), { transformToolPayloads: false }),
          this.messages,
        ),
      ui: (): AIV5Type.UIMessage[] => this.toAIV5UIMessages(this.inputPersisted.db()),
    },
    aiV6: {
      ui: () => this.toAIV6UIMessages(this.inputPersisted.db()),
    },

    /* @deprecated use list.getPersisted.input.aiV4.ui() */
    ui: (): UIMessageWithMetadata[] => this.toAIV4UIMessages(this.inputPersisted.db()),
    /* @deprecated use list.getPersisted.input.aiV4.core() */
    core: () =>
      aiV4UIMessagesToAIV4CoreMessages(
        this.toAIV4UIMessages(this.inputPersisted.db(), { transformToolPayloads: false }),
      ),
    aiV4: {
      ui: (): UIMessageWithMetadata[] => this.toAIV4UIMessages(this.inputPersisted.db()),
      core: (): CoreMessageV4[] =>
        aiV4UIMessagesToAIV4CoreMessages(
          this.toAIV4UIMessages(this.inputPersisted.db(), { transformToolPayloads: false }),
        ),
    },
  };

  private response = {
    db: (): MastraDBMessage[] => this.messages.filter(m => this.newResponseMessages.has(m)),
    v1: (): MastraMessageV1[] => convertToV1Messages(this.response.db()),

    aiV5: {
      ui: (): AIV5Type.UIMessage[] => this.toAIV5UIMessages(this.response.db()),
      model: (): AIV5ResponseMessage[] =>
        convertAIV5UIToModelMessages(
          this.toAIV5UIMessages(this.response.db(), { transformToolPayloads: false }),
          this.messages,
        ).filter(m => m.role === `tool` || m.role === `assistant`),
      modelContent: (stepNumber?: number): AIV5Type.StepResult<any>['content'] => {
        if (typeof stepNumber === 'number') {
          // Delegate to StepContentExtractor for step-specific content extraction
          return StepContentExtractor.extractStepContent(
            this.response.aiV5.ui(),
            stepNumber,
            this.response.aiV5.stepContent,
          );
        }

        return this.response.aiV5.model().map(this.response.aiV5.stepContent).flat();
      },
      stepContent: (message?: AIV5Type.ModelMessage): AIV5Type.StepResult<any>['content'] => {
        // Delegate to StepContentExtractor for content conversion
        return StepContentExtractor.convertToStepContent(message, this.messages, () =>
          this.response.aiV5.model().at(-1),
        );
      },
    },
    aiV6: {
      ui: () => this.toAIV6UIMessages(this.response.db()),
    },

    aiV4: {
      ui: (): UIMessageWithMetadata[] => this.toAIV4UIMessages(this.response.db()),
      core: (): CoreMessageV4[] =>
        aiV4UIMessagesToAIV4CoreMessages(this.toAIV4UIMessages(this.response.db(), { transformToolPayloads: false })),
    },
  };
  private responsePersisted = {
    db: (): MastraDBMessage[] => this.messages.filter(m => this.newResponseMessagesPersisted.has(m)),

    aiV5: {
      model: () =>
        convertAIV5UIToModelMessages(
          this.toAIV5UIMessages(this.responsePersisted.db(), { transformToolPayloads: false }),
          this.messages,
        ),
      ui: (): AIV5Type.UIMessage[] => this.toAIV5UIMessages(this.responsePersisted.db()),
    },
    aiV6: {
      ui: () => this.toAIV6UIMessages(this.responsePersisted.db()),
    },

    /* @deprecated use list.getPersisted.response.aiV4.ui() */
    ui: (): UIMessageWithMetadata[] => this.toAIV4UIMessages(this.responsePersisted.db()),
    aiV4: {
      ui: (): UIMessageWithMetadata[] => this.toAIV4UIMessages(this.responsePersisted.db()),
      core: (): CoreMessageV4[] =>
        aiV4UIMessagesToAIV4CoreMessages(
          this.toAIV4UIMessages(this.responsePersisted.db(), { transformToolPayloads: false }),
        ),
    },
  };

  public drainUnsavedMessages(): MastraDBMessage[] {
    const messages = this.messages.filter(m => this.newUserMessages.has(m) || this.newResponseMessages.has(m));
    this.newUserMessages.clear();
    this.newResponseMessages.clear();
    return messages.map(message => this.transformMessageForTranscript(message));
  }

  private transformToolStateDataForTranscript(data: unknown, phase: 'approval' | 'suspend'): unknown {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const stateData = data as Record<string, unknown>;
    const metadata = stateData.metadata ?? stateData.providerMetadata;
    const phaseTransform = getTransformedToolPayload(metadata, 'transcript', phase);
    const inputTransform = getTransformedToolPayload(metadata, 'transcript', 'input-available');
    const transformedArgs =
      phase === 'approval'
        ? hasTransformedToolPayload(phaseTransform)
          ? phaseTransform.transformed
          : hasTransformedToolPayload(inputTransform)
            ? inputTransform.transformed
            : undefined
        : hasTransformedToolPayload(inputTransform)
          ? inputTransform.transformed
          : hasTransformedToolPayload(phaseTransform)
            ? phaseTransform.transformed
            : undefined;
    const transformedSuspendPayload =
      phase === 'suspend' && hasTransformedToolPayload(phaseTransform) ? phaseTransform.transformed : undefined;

    return {
      ...stateData,
      ...(transformedArgs !== undefined ? { args: transformedArgs } : {}),
      ...(transformedSuspendPayload !== undefined ? { suspendPayload: transformedSuspendPayload } : {}),
    };
  }

  private transformMessageForTranscript(message: MastraDBMessage): MastraDBMessage {
    if (message.content?.format !== 2 || !message.content.parts) {
      return message;
    }

    let changed = false;
    const transformedByToolCallId = new Map<string, { args?: unknown; result?: unknown; errorText?: string }>();

    const parts = message.content.parts.map(part => {
      if (part.type === 'tool-invocation' && part.toolInvocation) {
        const inputTransform = getTransformedToolPayload(part.providerMetadata, 'transcript', 'input-available');
        const outputTransform =
          part.toolInvocation.state === 'result'
            ? (getTransformedToolPayload(part.providerMetadata, 'transcript', 'output-available') ??
              getTransformedToolPayload(part.providerMetadata, 'transcript', 'error'))
            : part.toolInvocation.state === 'output-error'
              ? getTransformedToolPayload(part.providerMetadata, 'transcript', 'error')
              : undefined;

        if (!inputTransform && !outputTransform) {
          return part;
        }

        changed = true;
        const transformedArgs = hasTransformedToolPayload(inputTransform)
          ? inputTransform.transformed
          : part.toolInvocation.args;
        const transformedResult =
          part.toolInvocation.state === 'result'
            ? hasTransformedToolPayload(outputTransform)
              ? outputTransform.transformed
              : part.toolInvocation.result
            : undefined;
        const transformedErrorText =
          part.toolInvocation.state === 'output-error'
            ? hasTransformedToolPayload(outputTransform)
              ? (outputTransform.transformed as string)
              : part.toolInvocation.errorText
            : undefined;
        transformedByToolCallId.set(part.toolInvocation.toolCallId, {
          args: transformedArgs,
          ...(part.toolInvocation.state === 'result' ? { result: transformedResult } : {}),
          ...(part.toolInvocation.state === 'output-error' ? { errorText: transformedErrorText } : {}),
        });

        return {
          ...part,
          toolInvocation: {
            ...part.toolInvocation,
            args: transformedArgs,
            ...(part.toolInvocation.state === 'result' ? { result: transformedResult } : {}),
            ...(part.toolInvocation.state === 'output-error' ? { errorText: transformedErrorText } : {}),
          },
        };
      }

      if (part.type === 'data-tool-call-suspended' || part.type === 'data-tool-call-approval') {
        changed = true;
        return {
          ...part,
          data: this.transformToolStateDataForTranscript(
            part.data,
            part.type === 'data-tool-call-suspended' ? 'suspend' : 'approval',
          ),
        };
      }

      return part;
    });

    const toolInvocations = message.content.toolInvocations?.map(invocation => {
      const transformed = transformedByToolCallId.get(invocation.toolCallId);
      if (!transformed) {
        return invocation;
      }

      const invocationState = invocation.state as string;
      changed = true;
      return {
        ...invocation,
        ...(transformed.args !== undefined ? { args: transformed.args } : {}),
        ...(invocation.state === 'result' && transformed.result !== undefined ? { result: transformed.result } : {}),
        ...(invocationState === 'output-error' && transformed.errorText !== undefined
          ? { errorText: transformed.errorText }
          : {}),
      };
    });

    const metadata =
      message.content.metadata && typeof message.content.metadata === 'object'
        ? { ...(message.content.metadata as Record<string, unknown>) }
        : message.content.metadata;
    if (metadata && typeof metadata === 'object') {
      for (const [key, phase] of [
        ['suspendedTools', 'suspend'],
        ['pendingToolApprovals', 'approval'],
      ] as const) {
        const toolStates = metadata[key];
        if (!toolStates || typeof toolStates !== 'object') {
          continue;
        }
        changed = true;
        metadata[key] = Object.fromEntries(
          Object.entries(toolStates as Record<string, unknown>).map(([toolName, state]) => [
            toolName,
            this.transformToolStateDataForTranscript(state, phase),
          ]),
        );
      }
    }

    if (!changed) {
      return message;
    }

    return {
      ...message,
      content: {
        ...message.content,
        parts,
        ...(toolInvocations ? { toolInvocations } : {}),
        ...(metadata ? { metadata } : {}),
      },
    };
  }

  public getEarliestUnsavedMessageTimestamp(): number | undefined {
    const unsavedMessages = this.messages.filter(m => this.newUserMessages.has(m) || this.newResponseMessages.has(m));
    if (unsavedMessages.length === 0) return undefined;
    // Find the earliest createdAt among unsaved messages
    return Math.min(...unsavedMessages.map(m => new Date(m.createdAt).getTime()));
  }

  /**
   * Check if a message is a new user or response message that should be saved.
   * Checks by message ID to handle cases where the message object may be a copy.
   */
  public isNewMessage(messageOrId: MastraDBMessage | string): boolean {
    return this.stateManager.isNewMessage(messageOrId);
  }

  /**
   * Replace a tool-invocation part matching the given toolCallId with the
   * provided result part. Walks backwards through messages to find the match.
   * If the message was already persisted (e.g. as a memory message), it is
   * moved to the response source so it will be re-saved.
   *
   * @returns true if the tool call was found and updated, false otherwise.
   */
  public updateToolInvocation(
    inputPart: Extract<MastraMessagePart, { type: 'tool-invocation' }>,
    metadata?: Record<string, unknown>,
  ): boolean {
    if (!inputPart.toolInvocation?.toolCallId) {
      return false;
    }
    const toolCallId = inputPart.toolInvocation.toolCallId;

    for (let m = this.messages.length - 1; m >= 0; m--) {
      const msg = this.messages[m]!;
      if (msg.role !== 'assistant' || !msg.content?.parts) continue;

      for (let i = 0; i < msg.content.parts.length; i++) {
        const part = msg.content.parts[i];
        if (part?.type === 'tool-invocation' && part.toolInvocation?.toolCallId === toolCallId) {
          // Cast to access providerExecuted/providerMetadata which exist at runtime but aren't in the base type
          const originalPart = part as typeof part & { providerExecuted?: boolean; providerMetadata?: unknown };
          const inputPartWithMeta = inputPart as typeof inputPart & {
            providerExecuted?: boolean;
            providerMetadata?: unknown;
          };

          const mergedProviderMetadata =
            originalPart.providerMetadata !== undefined || inputPartWithMeta.providerMetadata !== undefined
              ? ({
                  ...((originalPart.providerMetadata ?? {}) as Record<string, Record<string, AIV5Type.JSONValue>>),
                  ...((inputPartWithMeta.providerMetadata ?? {}) as Record<string, Record<string, AIV5Type.JSONValue>>),
                } as AIV5Type.ProviderMetadata)
              : undefined;

          msg.content.parts[i] = {
            ...inputPart,
            toolInvocation: {
              ...inputPart.toolInvocation,
              args: part.toolInvocation.args,
            },
            // Preserve providerExecuted from original call if not in result
            ...(originalPart.providerExecuted !== undefined && inputPartWithMeta.providerExecuted === undefined
              ? { providerExecuted: originalPart.providerExecuted }
              : {}),
            ...(mergedProviderMetadata !== undefined ? { providerMetadata: mergedProviderMetadata } : {}),
          };
          this.lastCreatedAt = Math.max(this.lastCreatedAt || 0, Date.now());
          this.updateLastCreatedAt(msg);

          // `backgroundTasks` is a per-toolCallId record — merge instead of
          // overwrite so multiple concurrent background dispatches on the
          // same assistant message don't clobber each other's metadata.
          const existingMeta = (msg.content.metadata ?? {}) as Record<string, unknown>;
          const incomingMeta = (metadata ?? {}) as Record<string, unknown>;
          const existingBgTasks = existingMeta.backgroundTasks as Record<string, unknown> | undefined;
          const incomingBgTasks = incomingMeta.backgroundTasks as Record<string, unknown> | undefined;
          const backgroundTasks = mergeBackgroundTasks(existingBgTasks, incomingBgTasks);

          msg.content.metadata = {
            ...existingMeta,
            ...incomingMeta,
            ...(backgroundTasks ? { backgroundTasks } : {}),
          };

          // Move the message to the response source so it gets
          // picked up by drainUnsavedMessages for re-saving.
          if (!this.stateManager.isResponseMessage(msg)) {
            this.stateManager.removeMessage(msg);
            this.stateManager.addToSource(msg, 'response');
          }

          return true;
        }
      }
    }
    this.logger?.warn(`updateToolInvocation: no matching tool call found for toolCallId=${toolCallId}`);
    return false;
  }

  public updateMessageMetadataByToolCallId(toolCallId: string, metadata: Record<string, unknown>): boolean {
    if (!toolCallId) {
      return false;
    }

    for (let m = this.messages.length - 1; m >= 0; m--) {
      const msg = this.messages[m]!;
      if (msg.role !== 'assistant' || !msg.content?.parts) continue;

      const hasToolCall = msg.content.parts.some(
        part => part?.type === 'tool-invocation' && part.toolInvocation?.toolCallId === toolCallId,
      );
      if (!hasToolCall) continue;

      const existingMeta = (msg.content.metadata ?? {}) as Record<string, unknown>;
      const incomingMeta = (metadata ?? {}) as Record<string, unknown>;
      const existingBgTasks = existingMeta.backgroundTasks as Record<string, unknown> | undefined;
      const incomingBgTasks = incomingMeta.backgroundTasks as Record<string, unknown> | undefined;
      const backgroundTasks = mergeBackgroundTasks(existingBgTasks, incomingBgTasks);

      msg.content.metadata = {
        ...existingMeta,
        ...incomingMeta,
        ...(backgroundTasks ? { backgroundTasks } : {}),
      };

      this.lastCreatedAt = Math.max(this.lastCreatedAt || 0, Date.now());
      this.updateLastCreatedAt(msg);

      if (!this.stateManager.isResponseMessage(msg)) {
        this.stateManager.removeMessage(msg);
        this.stateManager.addToSource(msg, 'response');
      }

      return true;
    }

    this.logger?.warn(`updateMessageMetadataByToolCallId: no matching tool call found for toolCallId=${toolCallId}`);
    return false;
  }

  /**
   * Append a `step-start` boundary to the last assistant message.
   * This marks the beginning of a new loop iteration so that
   * `convertToModelMessages` splits sequential tool-call turns into
   * separate message blocks instead of collapsing them into one.
   *
   * Respects sealed messages (post-observation) — if the last assistant
   * message is sealed, the step-start is not added.
   *
   * If the message was loaded from memory it is moved to the response
   * source so the updated content is re-saved.
   */
  public stepStart(): boolean {
    const lastMsg = this.messages[this.messages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant' || !lastMsg.content?.parts) {
      return false;
    }

    if (MessageMerger.isSealed(lastMsg)) {
      return false;
    }

    // Don't add a duplicate step-start
    const lastPart = lastMsg.content.parts[lastMsg.content.parts.length - 1];
    if (lastPart?.type === 'step-start') {
      return false;
    }

    lastMsg.content.parts.push(stampPart({ type: 'step-start' as const }));

    // Ensure the mutated message is persisted
    if (!this.stateManager.isResponseMessage(lastMsg)) {
      this.stateManager.removeMessage(lastMsg);
      this.stateManager.addToSource(lastMsg, 'response');
    }

    return true;
  }

  public markResponseMessageBoundary(messageId?: string): boolean {
    const message = messageId
      ? this.messages.find(message => message.id === messageId)
      : [...this.messages].reverse().find(message => message.role === 'assistant');

    if (!message || message.role !== 'assistant') {
      return false;
    }

    message.content.metadata = {
      ...(message.content.metadata ?? {}),
      mastra: {
        ...((message.content.metadata?.mastra as Record<string, unknown> | undefined) ?? {}),
        responseBoundary: true,
      },
    };

    if (!this.stateManager.isResponseMessage(message)) {
      this.stateManager.removeMessage(message);
      this.stateManager.addToSource(message, 'response');
    }

    return true;
  }

  public enrichLastStepStart(model: string): boolean {
    const lastMsg = this.messages[this.messages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant' || !lastMsg.content?.parts) {
      return false;
    }

    if (MessageMerger.isSealed(lastMsg)) {
      return false;
    }

    for (let i = lastMsg.content.parts.length - 1; i >= 0; i--) {
      const part = lastMsg.content.parts[i];
      if (part?.type !== 'step-start') {
        continue;
      }

      // Only stamp step-starts that haven't already been attributed. A prior
      // iteration (or a re-used message loaded from memory) may have already
      // stamped its model, and overwriting it would mis-attribute history.
      if (part.model) {
        return false;
      }

      part.model = model;

      if (!this.stateManager.isResponseMessage(lastMsg)) {
        this.stateManager.removeMessage(lastMsg);
        this.stateManager.addToSource(lastMsg, 'response');
      }

      return true;
    }

    return false;
  }

  public getSystemMessages(tag?: string): CoreMessageV4[] {
    if (tag) {
      return this.taggedSystemMessages[tag] || [];
    }
    return this.systemMessages;
  }

  /**
   * Get all system messages (both tagged and untagged)
   * @returns Array of all system messages
   */
  public getAllSystemMessages(): CoreMessageV4[] {
    return [...this.systemMessages, ...Object.values(this.taggedSystemMessages).flat()];
  }

  /**
   * Clear system messages, optionally for a specific tag
   * @param tag - If provided, only clears messages with this tag. Otherwise clears untagged messages.
   */
  public clearSystemMessages(tag?: string): this {
    if (tag) {
      delete this.taggedSystemMessages[tag];
    } else {
      this.systemMessages = [];
    }
    return this;
  }

  /**
   * Replace the untagged system message bucket with the provided array while
   * leaving tagged system message buckets (owned by other processors) intact.
   * @param messages - Array of system messages to set as untagged
   */
  public replaceAllSystemMessages(messages: CoreMessageV4[]): this {
    this.systemMessages = [];

    for (const message of messages) {
      if (message.role !== 'system') continue;
      this.systemMessages.push(message);
    }

    return this;
  }

  public addSystem(
    messages:
      | CoreMessageV4
      | CoreMessageV4[]
      | AIV6Type.ModelMessage
      | AIV6Type.ModelMessage[]
      | AIV5Type.ModelMessage
      | AIV5Type.ModelMessage[]
      | MastraDBMessage
      | MastraDBMessage[]
      | string
      | string[]
      | null,
    tag?: string,
  ) {
    if (!messages) return this;
    for (const message of Array.isArray(messages) ? messages : [messages]) {
      this.addOneSystem(message, tag);
    }
    return this;
  }

  private addOneSystem(
    message: CoreMessageV4 | AIV6Type.ModelMessage | AIV5Type.ModelMessage | MastraDBMessage | string,
    tag?: string,
  ) {
    const coreMessage = systemMessageToAIV4Core(message);

    if (coreMessage.role !== `system`) {
      throw new Error(
        `Expected role "system" but saw ${coreMessage.role} for message ${JSON.stringify(coreMessage, null, 2)}`,
      );
    }

    if (tag && !this.isDuplicateSystem(coreMessage, tag)) {
      this.taggedSystemMessages[tag] ||= [];
      this.taggedSystemMessages[tag].push(coreMessage);
      if (this.isRecording) {
        this.recordedEvents.push({
          type: 'addSystem',
          tag,
          message: coreMessage,
        });
      }
    } else if (!tag && !this.isDuplicateSystem(coreMessage)) {
      this.systemMessages.push(coreMessage);
      if (this.isRecording) {
        this.recordedEvents.push({
          type: 'addSystem',
          message: coreMessage,
        });
      }
    }
  }

  private isDuplicateSystem(message: CoreMessageV4, tag?: string) {
    if (tag) {
      if (!this.taggedSystemMessages[tag]) return false;
      return this.taggedSystemMessages[tag].some(
        m =>
          CacheKeyGenerator.fromAIV4CoreMessageContent(m.content) ===
          CacheKeyGenerator.fromAIV4CoreMessageContent(message.content),
      );
    }
    return this.systemMessages.some(
      m =>
        CacheKeyGenerator.fromAIV4CoreMessageContent(m.content) ===
        CacheKeyGenerator.fromAIV4CoreMessageContent(message.content),
    );
  }

  private getMessageById(id: string) {
    return this.messages.find(m => m.id === id);
  }

  private shouldReplaceMessage(message: MastraDBMessage): { exists: boolean; shouldReplace?: boolean; id?: string } {
    if (!this.messages.length) return { exists: false };

    if (!(`id` in message) || !message?.id) {
      return { exists: false };
    }

    const existingMessage = this.getMessageById(message.id);
    if (!existingMessage) return { exists: false };

    return {
      exists: true,
      shouldReplace: !messagesAreEqual(existingMessage, message),
      id: existingMessage.id,
    };
  }

  private addOne(message: MessageInput, messageSource: MessageSource, options: MessageListAddOptions = {}) {
    if (
      (!(`content` in message) ||
        (!message.content &&
          // allow empty strings
          typeof message.content !== 'string')) &&
      (!(`parts` in message) || !message.parts)
    ) {
      throw new MastraError({
        id: 'INVALID_MESSAGE_CONTENT',
        domain: ErrorDomain.AGENT,
        category: ErrorCategory.USER,
        text: `Message with role "${message.role}" must have either a 'content' property (string or array) or a 'parts' property (array) that is not empty, null, or undefined. Received message: ${JSON.stringify(message, null, 2)}`,
        details: {
          role: message.role as string,
          messageSource,
          hasContent: 'content' in message,
          hasParts: 'parts' in message,
        },
      });
    }

    if (message.role === `system`) {
      // In the past system messages were accidentally stored in the db. these should be ignored because memory is not supposed to store system messages.
      if (messageSource === `memory`) return null;

      // Check if the message is in a supported format for system messages
      const isSupportedSystemFormat =
        TypeDetector.isAIV4CoreMessage(message) ||
        TypeDetector.isAIV6CoreMessage(message) ||
        TypeDetector.isAIV5CoreMessage(message) ||
        TypeDetector.isMastraDBMessage(message);

      if (isSupportedSystemFormat) {
        return this.addSystem(message);
      }

      // if we didn't add the message and we didn't ignore this intentionally, then it's a problem!
      throw new MastraError({
        id: 'INVALID_SYSTEM_MESSAGE_FORMAT',
        domain: ErrorDomain.AGENT,
        category: ErrorCategory.USER,
        text: `Invalid system message format. System messages must be CoreMessage format with 'role' and 'content' properties. The content should be a string or valid content array.`,
        details: {
          messageSource,
          receivedMessage: JSON.stringify(message, null, 2),
        },
      });
    }

    const messageV2 = convertInputToMastraDBMessage(message, messageSource, this.createAdapterContext());
    const signalMetadata =
      messageV2.role === 'signal'
        ? (messageV2.content.metadata?.signal as { acceptedAt?: string; createdAt?: string } | undefined)
        : undefined;
    if (messageSource === 'input' && messageV2.role === 'signal' && !signalMetadata?.acceptedAt) {
      const acceptedAt = signalMetadata?.createdAt ?? messageV2.createdAt.toISOString();
      messageV2.createdAt = this.generateCreatedAt(messageSource, messageV2.createdAt);
      messageV2.content.metadata = {
        ...messageV2.content.metadata,
        signal: {
          ...signalMetadata,
          createdAt: messageV2.createdAt.toISOString(),
          acceptedAt,
        },
      };
    }

    const { exists, shouldReplace, id } = this.shouldReplaceMessage(messageV2);

    const latestSealedIndex = this.messages.findLastIndex(message => MessageMerger.isSealed(message));
    const latestMessage = this.messages.at(-1);
    const latestMessageIndex = this.messages.length - 1;
    const latestMessageIsAfterSealedBoundary = latestSealedIndex === -1 || latestMessageIndex > latestSealedIndex;

    if (messageSource === `memory`) {
      for (const existingMessage of this.messages) {
        // don't double store any messages
        if (messagesAreEqual(existingMessage, messageV2)) {
          return;
        }
      }
    }

    const replacementTarget = exists && id ? this.messages.find(m => m.id === id) : undefined;
    const hasSealedReplacementTarget = !!replacementTarget && MessageMerger.isSealed(replacementTarget);

    // Keep this replacement-target guard here instead of MessageMerger.shouldMerge().
    // shouldMerge() only decides whether to append to the latest assistant message,
    // but replace-by-id can target an older sealed message elsewhere in the list.
    const isLatestFromMemory = latestMessage ? this.memoryMessages.has(latestMessage) : false;
    const shouldMerge =
      options.merge !== false &&
      latestMessageIsAfterSealedBoundary &&
      !hasSealedReplacementTarget &&
      MessageMerger.shouldMerge(latestMessage, messageV2, messageSource, isLatestFromMemory, this._agentNetworkAppend);

    if (shouldMerge && latestMessage) {
      // Delegate merge logic to MessageMerger
      MessageMerger.merge(latestMessage, messageV2);
      this.updateLastCreatedAt(latestMessage);

      // If latest message gets appended to, it should be added to the proper source
      this.pushMessageToSource(latestMessage, messageSource);
    }
    // Else the last message and this message are not both assistant messages OR an existing message has been updated and should be replaced. add a new message to the array or update an existing one.
    else {
      let existingIndex = -1;
      if (shouldReplace) {
        existingIndex = this.messages.findIndex(m => m.id === id);
      }
      const existingMessage = existingIndex !== -1 && this.messages[existingIndex];

      if (shouldReplace && existingMessage) {
        const existingIsAtOrBeforeSealedBoundary = latestSealedIndex !== -1 && existingIndex <= latestSealedIndex;

        // If the existing message is sealed (e.g., after observation), don't replace it.
        // Instead, generate a new ID for the incoming message and add it as a new message.
        if (MessageMerger.isSealed(existingMessage)) {
          // Find the last part with sealedAt metadata in the EXISTING message.
          // The existing message has the seal boundary marker from insertObservationMarker.
          const existingParts = existingMessage.content?.parts || [];
          let sealedPartCount = 0;

          for (let i = existingParts.length - 1; i >= 0; i--) {
            const part = existingParts[i] as { metadata?: { mastra?: { sealedAt?: number } } };
            if (part?.metadata?.mastra?.sealedAt) {
              // The seal is at index i, so sealed content is parts 0 through i (inclusive)
              sealedPartCount = i + 1;
              break;
            }
          }

          // If no sealedAt found, use the entire existing message length as the boundary
          if (sealedPartCount === 0) {
            sealedPartCount = existingParts.length;
          }

          // Get parts from incoming message that are beyond the sealed boundary
          const incomingParts = messageV2.content.parts;

          let newParts: typeof incomingParts;

          if (incomingParts.length <= sealedPartCount) {
            // Incoming message has fewer or equal parts than the sealed boundary.
            // Check if these are truly stale (same content as the sealed message) or
            // new content flushed independently (e.g., text deltas flushed with the
            // same messageId but only containing a text part).
            if (messagesAreEqual(existingMessage, messageV2)) {
              // Stale message, ignore - don't replace, don't create new
              return this;
            }
            // Not stale — these are fresh parts (e.g., a text flush). Treat all as new.
            newParts = incomingParts;
          } else {
            newParts = incomingParts.slice(sealedPartCount);
          }

          // Only create a new message if there are actually new parts
          if (newParts.length > 0) {
            // Generate a new ID for the incoming message
            messageV2.id = this.generateMessageId?.({ idType: 'message', source: 'memory' }) ?? randomUUID();
            // Replace the parts with only the new ones
            messageV2.content.parts = newParts;
            // Ensure the new message has a timestamp after the sealed message
            if (messageV2.createdAt <= existingMessage.createdAt) {
              messageV2.createdAt = new Date(existingMessage.createdAt.getTime() + 1);
            }
            this.messages.push(messageV2);
          }
          // If no new parts, don't add anything (the sealed message already has all the content)
        } else if (existingIsAtOrBeforeSealedBoundary) {
          messageV2.id = this.generateMessageId?.({ idType: 'message', source: 'memory' }) ?? randomUUID();
          if (messageV2.createdAt <= existingMessage.createdAt) {
            messageV2.createdAt = new Date(existingMessage.createdAt.getTime() + 1);
          }
          this.messages.push(messageV2);
        } else {
          const isExistingFromMemory = this.memoryMessages.has(existingMessage);
          const shouldMergeIntoExisting =
            options.merge !== false &&
            MessageMerger.shouldMerge(
              existingMessage,
              messageV2,
              messageSource,
              isExistingFromMemory,
              this._agentNetworkAppend,
            );
          if (shouldMergeIntoExisting) {
            MessageMerger.merge(existingMessage, messageV2);
            this.updateLastCreatedAt(existingMessage);
            this.pushMessageToSource(existingMessage, messageSource);
            // Sort messages and return early — existingMessage stays in messages[] and its Sets
            this.messages.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
            return this;
          }
          this.messages[existingIndex] = messageV2;
        }
      } else if (!exists) {
        this.messages.push(messageV2);
      }

      this.pushMessageToSource(messageV2, messageSource);
    }

    for (const storedMessage of this.messages) {
      this.updateLastCreatedAt(storedMessage);
    }

    // make sure messages are always stored in order of when they were created!
    this.messages.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

    return this;
  }

  private pushMessageToSource(messageV2: MastraDBMessage, messageSource: MessageSource) {
    this.stateManager.addToSource(messageV2, messageSource);
  }

  private lastCreatedAt?: number;

  private updateLastCreatedAt(message: MastraDBMessage): void {
    // Message-level createdAt controls transcript ordering and OM observation boundaries.
    // Part timestamps are event metadata within a message and must not advance the
    // ordering watermark used to timestamp later messages/signals.
    this.lastCreatedAt = Math.max(this.lastCreatedAt || 0, message.createdAt.getTime());
  }

  // this makes sure messages added in order will always have a date atleast 1ms apart.
  private generateCreatedAt(messageSource: MessageSource, start?: unknown): Date {
    // Normalize timestamp
    const startDate: Date | undefined =
      start instanceof Date
        ? start
        : typeof start === 'string' || typeof start === 'number'
          ? new Date(start)
          : undefined;

    if (startDate && !this.lastCreatedAt) {
      this.lastCreatedAt = startDate.getTime();
      return startDate;
    }

    if (startDate && messageSource === `memory`) {
      // Preserve user-provided timestamps for memory messages to avoid re-ordering
      // Messages without timestamps will fall through to get generated incrementing timestamps
      return startDate;
    }

    const now = new Date();
    const nowTime = startDate?.getTime() || now.getTime();
    const lastTime = this.lastCreatedAt || 0;

    // make sure our new message is created later than the latest known ordering timestamp
    // it's expected that messages are added to the list in order if they don't have a createdAt date on them
    if (nowTime <= lastTime) {
      const newDate = new Date(lastTime + 1);
      this.lastCreatedAt = newDate.getTime();
      return newDate;
    }

    this.lastCreatedAt = nowTime;
    return startDate ?? now;
  }

  private newMessageId(role?: string): string {
    if (this.generateMessageId) {
      return this.generateMessageId({
        idType: 'message',
        source: 'agent',
        threadId: this.memoryInfo?.threadId,
        resourceId: this.memoryInfo?.resourceId,
        role,
      });
    }
    return randomUUID();
  }

  private createAdapterContext() {
    return {
      memoryInfo: this.memoryInfo,
      newMessageId: () => this.newMessageId(),
      generateCreatedAt: (messageSource: MessageSource, start?: unknown) =>
        this.generateCreatedAt(messageSource, start),
      dbMessages: this.messages,
    };
  }
}
